var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as L from 'leaflet';
import { DateTime } from 'luxon';
import { fetchMDXData } from './lib/utils/fetchMDXData';
import { TIME_CHANGED, TIME_PLAYBACK_TICKED } from './events';
/**
 * The click layer.
 */
export class LayerClick extends L.Polygon {
    /**
     * Create a click layer instance.
     * @param options - The click layer options
     * @param widget - The M.A.A.P. instance.
     */
    constructor(options, widget) {
        // call constructor of L.Polygon
        super(options.latlngs, options.polygonOptions);
        // create the url schema with set start and end times
        this._urlSchema = this.setUrlSchemaParams(options.urlSchema, {
            start: DateTime.fromISO(options.start, { zone: 'utc' }).toFormat(options.timeFormat),
            end: DateTime.fromISO(options.end, { zone: 'utc' }).toFormat(options.timeFormat),
        });
        // assign options to instance
        this._locLabel = options.locLabel;
        this._popupLabel = options.popupLabel;
        this._popupLabelTimeFormat = options.popupLabelTimeFormat;
        this._param = options.param;
        // assign widget to instance
        this.maapInstance = widget;
        // create popup
        this.initPopup();
        // attach event handlers
        this.on('add', this.onAddLayerClick);
        this.on('click', this.onClick);
    }
    /**
     * Initialize popup and add markup.
     */
    initPopup() {
        // create popup
        const className = 'maap_p-popup';
        this._popup = new L.Popup({ className });
        // create main content container
        const contentEl = document.createElement('div');
        contentEl.classList.add(`${className}__content`);
        // create title element
        this._titleEl = document.createElement('h4');
        this._titleEl.classList.add(`${className}__title`);
        // create value container
        this._valueEl = document.createElement('div');
        this._valueEl.classList.add(`${className}__value`);
        // create location container
        this._locationEl = document.createElement('div');
        this._locationEl.classList.add(`${className}__location`);
        // append title, value and location containers to content
        contentEl.appendChild(this._titleEl);
        contentEl.appendChild(this._valueEl);
        contentEl.appendChild(this._locationEl);
        // set popup content
        this._popup.setContent(contentEl);
    }
    /**
     * Event handler to be triggerd when added to map.
     */
    onAddLayerClick() {
        this.maapInstance.on(`${TIME_PLAYBACK_TICKED} ${TIME_CHANGED}`, () => {
            if (this._popup && this._popup.isOpen()) {
                this.updatePopupValue();
            }
        });
    }
    /**
     * Event handler to be triggered when clicked.
     * @param event - The leaflet click event.
     */
    onClick(event) {
        this._latlng = event.latlng;
        this.updatePopupPosition();
        if (this._popup) {
            this._popup.openOn(this._map);
            this.updatePopupValue();
        }
    }
    /**
     * Update the popup's position.
     */
    updatePopupPosition() {
        var _a;
        if (!this._latlng)
            return;
        const { lat, lng } = this._latlng;
        // set popoup location
        (_a = this._popup) === null || _a === void 0 ? void 0 : _a.setLatLng([lat, lng]);
        // set html content of popup
        if (this._locationEl) {
            // eslint-disable-next-line
            this._locationEl.innerHTML =
                `${this._locLabel} ${lat.toFixed(3)}, ${lng.toFixed(3)}`;
        }
        if (this._valueEl) {
            this._valueEl.innerHTML = 'loading...';
        }
    }
    /**
     * Update the popup's value/content.
     */
    updatePopupValue() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._latlng)
                return;
            const { lat, lng } = this._latlng;
            // set coordinate parameters in URL schema
            const url = this.setUrlSchemaParams(this._urlSchema, {
                lat: lat.toString(),
                lon: lng.toString(),
            });
            // get data from mdx api
            const data = yield fetchMDXData(url);
            const dtCurrent = this.maapInstance.time.currentTime();
            if (!dtCurrent)
                return;
            if (this._valueEl) {
                const timeKey = dtCurrent
                    .startOf('month')
                    .toFormat('yyyy-LL-dd HH:mm:ss');
                const value = timeKey in data ? data[timeKey][this._param.key] : '--';
                this._valueEl.innerHTML = `${this._param.desc} <strong>${value}</strong> ${this._param.unit}`;
            }
            if (this._titleEl) {
                // eslint-disable-next-line
                this._titleEl.innerHTML =
                    `${this._popupLabel} ${dtCurrent.toFormat(this._popupLabelTimeFormat)}`;
            }
        });
    }
    /**
     * Replace url schema placeholders with parameter values.
     * @param urlSchema - URL Schema with placeholders for parameters
     * @param params - Parameters as key value pairs
     * @returns URL Schema with set parameters
     */
    setUrlSchemaParams(urlSchema, params) {
        for (const key in params) {
            const value = params[key];
            urlSchema = urlSchema.replace(`{${key}}`, value);
        }
        return urlSchema;
    }
}
//# sourceMappingURL=LayerClick.js.map